<template>
    <div class="parallax-background">
        <h2 class="text-center mt-0">Trustpilot</h2>
        <UISwiper :active="enableSwipe" class="cards-row d-flex" @swipeLeft="nextReview" @swipeRight="previousReview">
            <TrustpilotCard
                v-for="(review, index) in reviews"
                :key="review.id"
                :class="getClass(index)"
                :review="review"
            />
        </UISwiper>

        <div class="d-flex justify-content-center mt-1 navigation">
            <a class="d-flex" @click="previousReview">
                <nuxt-resource
                    class="arrow left mr-3"
                    :class="{ disabled: shownReview <= 0 }"
                    src="images/about-page/arrow-long.svg"
                    alt="Left arrow"
                />
            </a>
            <p>0{{ shownReview + 1 }} - 0{{ maxReviews }}</p>
            <a class="d-flex" @click="nextReview">
                <nuxt-resource
                    class="arrow right ml-3"
                    :class="{ disabled: shownReview + 1 >= maxReviews }"
                    src="images/about-page/arrow-long.svg"
                    alt="Right arrow"
                />
            </a>
        </div>
    </div>
</template>

<script>
import { UISwiper } from '@dundle/ui/components';
import TrustpilotCard from '~/components/trustpilot/TrustpilotCard';

export default {
    components: {
        TrustpilotCard,
        UISwiper,
    },
    props: {
        reviews: {
            type: Array,
            required: true,
        },
        shownAmount: {
            type: Number,
            required: false,
            default: 5,
        },
    },
    data() {
        return {
            enableSwipe: false,
            shownReview: 0,
            direction: 0,
            onCooldown: false,
        };
    },
    computed: {
        maxReviews() {
            if (this.reviews.length <= 5) {
                return this.reviews.length;
            } else {
                return 5;
            }
        },
    },
    mounted() {
        if (this.$device.isMobile()) {
            this.enableSwipe = true;
        }
    },
    methods: {
        nextReview() {
            if (this.shownReview + 1 >= this.maxReviews || this.onCooldown) return;
            this.direction = 1;
            this.shownReview++;
            this.onCooldown = true;
            setTimeout(() => (this.onCooldown = false), 1000);
        },
        previousReview() {
            if (this.shownReview <= 0 || this.onCooldown) return;
            this.direction = 0;
            this.shownReview--;
            this.onCooldown = true;
            setTimeout(() => (this.onCooldown = false), 1000);
        },
        getClass(index) {
            if (!this.direction && index === this.shownReview + 1) {
                return 'right-out';
            }
            if (index < this.shownReview) {
                return 'left-card';
            } else if (index > this.shownReview) {
                return 'right-card';
            } else {
                if (this.direction) {
                    return 'middle-card-in';
                }
                return 'middle-card-out';
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@keyframes slidein {
    from {
        transform: translateX(700%);
    }

    to {
        transform: translateX(0%);
    }
}

@keyframes slideout700 {
    from {
        transform: translateX(0%);
    }

    to {
        transform: translateX(-700%);
    }
}

@keyframes slideout112 {
    from {
        transform: translateX(0%);
    }

    to {
        transform: translateX(-112%);
    }
}

@keyframes slideback {
    from {
        transform: translateX(-700%);
    }

    to {
        transform: translateX(0%);
    }
}

@keyframes slideforwards700 {
    from {
        transform: translateX(0%);
    }

    to {
        transform: translateX(700%);
    }
}

@keyframes slideforwards112 {
    from {
        transform: translateX(0%);
    }

    to {
        transform: translateX(112%);
    }
}

.parallax-background {
    // Enable when parallax is fixed
    // transform-style: preserve-3d;
    padding-top: 4rem;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;

        background-image: url(https://images.dundle.com/resources/images/about-page/pattern-dundle-black.svg);
        background-position: center center;
        background-size: 125px 131px;
        opacity: 0.07;
        background-repeat: repeat;

        // Enable when parallax is fixed
        // transform: translateZ(-2px) scale(1.5);
    }

    @media screen and (min-width: 768px) {
        padding: 5rem 0 5rem 0;
    }

    @media (min-width: 576px) and (max-width: 768px) {
        padding: 2rem 0 2rem 0;
    }
}

h2 {
    font-size: 1.8rem;
}

.arrow {
    position: relative;
    top: 3px;

    &:hover {
        cursor: pointer;
        filter: invert(34%) sepia(69%) saturate(1216%) hue-rotate(175deg) brightness(97%) contrast(108%);

        &.disabled {
            cursor: auto;
            filter: none;
        }
    }
    &.left {
        transform: rotateZ(180deg);
    }

    &.disabled {
        opacity: 0.2;
    }
}

.navigation {
    margin-bottom: 4rem;
    max-height: 15px;
    min-width: 80%;

    p {
        color: $color-primary-dark;
    }
}

h2 {
    @media screen and (max-width: 768px) {
        font-size: 1.3rem;
        line-height: 1.333rem;
    }
}

.cards-row {
    align-items: center;
    justify-content: center;
    overflow-x: scroll;

    padding-bottom: 4rem;
    padding-top: 2rem;

    &::-webkit-scrollbar {
        display: none;
    }
}

.middle-card-in {
    animation: slidein 1s;
    animation-fill-mode: forwards;
    transition-timing-function: cubic-bezier(0.1, 0.7, 1, 0.1);
    z-index: 1;
}

.middle-card-out {
    animation: slideback 1s;
    animation-fill-mode: forwards;
    transition-timing-function: cubic-bezier(0.1, 0.7, 1, 0.1);
}

.left-card {
    position: absolute;
    transform: translateX(-700%);

    animation: slideout112 1s;
    animation-fill-mode: forwards;
    transition-timing-function: cubic-bezier(0.1, 0.7, 1, 0.1);
    @media screen and (min-width: 768px) {
        animation: slideout700 1s;
        animation-fill-mode: forwards;
        transition-timing-function: cubic-bezier(0.1, 0.7, 1, 0.1);
    }
}

.right-out {
    position: absolute;
    animation: slideforwards112 1s;
    animation-fill-mode: forwards;
    transition-timing-function: cubic-bezier(0.1, 0.7, 1, 0.1);
    @media screen and (min-width: 768px) {
        animation: slideforwards700 1s;
        animation-fill-mode: forwards;
        transition-timing-function: cubic-bezier(0.1, 0.7, 1, 0.1);
    }
    z-index: 1;
}

.right-card {
    position: absolute;
    transform: translateX(112%);
    @media screen and (min-width: 768px) {
        transform: translateX(700%);
    }
}

.arrow-fill {
    width: 102px;
}
</style>
